import { useState, useEffect, useCallback, useRef } from 'react';
import { getProcessingStatus, retryProcessing as retryProcessingApi, ProcessingStatus } from '../utils/processingApi';
import { logger } from '../utils/logger';

export interface UseProcessingStatusReturn {
  processingStatuses: { [catalogId: string]: ProcessingStatus };
  startPolling: (catalogId: string, ownerId?: string | null) => void;
  stopPolling: (catalogId: string) => void;
  retryProcessing: (catalogId: string) => void;
}

const POLLING_INTERVAL = 2000; // 2 seconds
export const MAX_POLLING_TIME = 300000; // 5 minutes

/**
 * Pure helper: has the polling window for this catalog exceeded MAX_POLLING_TIME?
 * Exported for test access. Uses strict greater-than so the boundary case
 * (exactly at the cap) doesn't synthesize a premature timeout.
 */
export const isPollingExpired = (startTime: number | undefined, now: number): boolean => {
  if (!startTime) return false;
  return now - startTime > MAX_POLLING_TIME;
};

/**
 * Pure helper: derive the in-memory 'TIMEOUT' status from the last server-reported
 * status when the polling cap is hit. 'TIMEOUT' is synthesized client-side —
 * the server never emits it — so consumers (e.g. useMultipartUpload) can offer a
 * "still processing — refresh later" affordance distinct from a real 'FAILED'.
 */
export const synthesizeTimeoutStatus = (lastSeen: ProcessingStatus): ProcessingStatus => ({
  ...lastSeen,
  status: 'TIMEOUT',
  error: 'Processing timeout — please refresh and try again later',
});

export const useProcessingStatus = (): UseProcessingStatusReturn => {
  const [processingStatuses, setProcessingStatuses] = useState<{ [catalogId: string]: ProcessingStatus }>({});
  const pollingIntervals = useRef<{ [catalogId: string]: NodeJS.Timeout }>({});
  const pollingStartTimes = useRef<{ [catalogId: string]: number }>({});

  const updateStatus = useCallback((catalogId: string, status: ProcessingStatus) => {
    setProcessingStatuses(prev => ({
      ...prev,
      [catalogId]: status
    }));
  }, []);

  const stopPolling = useCallback((catalogId: string) => {
    if (pollingIntervals.current[catalogId]) {
      clearInterval(pollingIntervals.current[catalogId]);
      delete pollingIntervals.current[catalogId];
      delete pollingStartTimes.current[catalogId];
    }
  }, []);

  const ownerIdMap = useRef<{ [catalogId: string]: string | null | undefined }>({});

  const pollStatus = useCallback(async (catalogId: string) => {
    try {
      const status = await getProcessingStatus(catalogId, ownerIdMap.current[catalogId]);
      updateStatus(catalogId, status);

      // Stop polling if processing is complete, failed, or stuck in uploading
      if (status.status === 'READY' || status.status === 'FAILED') {
        stopPolling(catalogId);
      }

      // Stop polling for UPLOADING status after 30 seconds (likely abandoned upload)
      if (status.status === 'UPLOADING') {
        const startTime = pollingStartTimes.current[catalogId];
        if (startTime && Date.now() - startTime > 30000) {
          stopPolling(catalogId);
        }
      }

      // Stop polling if max time exceeded — synthesize a TIMEOUT status (distinct
      // from FAILED) so the UI can show a "still processing — refresh later"
      // affordance instead of a hard-error message.
      const startTime = pollingStartTimes.current[catalogId];
      if (isPollingExpired(startTime, Date.now())) {
        stopPolling(catalogId);
        updateStatus(catalogId, synthesizeTimeoutStatus(status));
      }
    } catch (error) {
      logger.error(`Failed to poll processing status for ${catalogId}`, {
        error: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });

      // If we get a 404, the catalog might not exist yet - continue polling
      // If we get a 401, stop polling and update status
      if (error.response?.status === 401) {
        stopPolling(catalogId);
        updateStatus(catalogId, {
          catalogId,
          status: 'FAILED',
          progress: 0,
          error: 'Authentication required - please log in again'
        });
      }
      // Continue polling on other errors (404, 500, etc.)
    }
  }, [updateStatus, stopPolling]);

  const startPolling = useCallback((catalogId: string, ownerId?: string | null) => {
    // Don't start if already polling
    if (pollingIntervals.current[catalogId]) {
      return;
    }

    ownerIdMap.current[catalogId] = ownerId;
    pollingStartTimes.current[catalogId] = Date.now();

    // Initial poll
    pollStatus(catalogId);

    // Set up interval polling
    pollingIntervals.current[catalogId] = setInterval(() => {
      pollStatus(catalogId);
    }, POLLING_INTERVAL);
  }, [pollStatus]);

  const retryProcessing = useCallback(async (catalogId: string) => {
    try {
      await retryProcessingApi(catalogId);
      // Restart polling after retry
      startPolling(catalogId);
    } catch (error) {
      logger.error(`Failed to retry processing for ${catalogId}`, error);
    }
  }, [startPolling]);

  // Cleanup on unmount
  useEffect(() => {
    const intervals = pollingIntervals;
    return () => {
      Object.values(intervals.current).forEach(clearInterval);
    };
  }, []);

  return {
    processingStatuses,
    startPolling,
    stopPolling,
    retryProcessing,
  };
};
